import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { formatDelayHours } from '@/lib/delay-limits/hours';
import { delayErrorKey } from '@/lib/delay/api';
import type { ICaseFlightDelay, IFlightDelayOutOfPolicy } from '@/lib/delay/types';
import { useDismissOutOfPolicy } from '@/lib/delay/use-delay';
import { formatFlightDateTime } from '@/lib/disruptions/airport-tz';

// One flight's out-of-policy provider report (delay-limits U2) — what was reported, the
// maximum, "not applied", source + airport-local time, and the operator's two answers:
// «Declarar cancelado» (the caller opens the declaration on CANCELLED) and «Descartar»
// (`POST …/delay/out-of-policy/dismiss`, pending / error; the answer folds into the
// caches, so the line goes). Shared by the header alert and the delay panel. `canAct`
// (AIRPORT_OPERATOR+, the BFF's gate) hides both answers otherwise. Tokens only; the
// buttons are 44 px targets below `sm`.

/** A 44 px touch target on a phone, compact from `sm`. */
export const OUT_OF_POLICY_BUTTON = 'h-11 px-3 text-xs sm:h-7 sm:px-2';

export function OutOfPolicyLine({
  delay,
  report,
  disruptionUrn,
  label,
  canAct,
  onCancel,
  className,
  testId = 'out-of-policy-alert',
}: {
  delay: ICaseFlightDelay;
  report: IFlightDelayOutOfPolicy;
  disruptionUrn: string | undefined;
  /** The flight, when several are flagged. */
  label: string | null;
  canAct: boolean;
  onCancel: () => void;
  className?: string;
  testId?: string;
}) {
  const { t } = useTranslation('disruptions');
  const dismiss = useDismissOutOfPolicy(delay.caseUrn, disruptionUrn);
  const failure = dismiss.isError ? delayErrorKey(dismiss.error) : null;
  return (
    <div
      className={cn(
        'flex max-w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-1.5 text-xs',
        className,
      )}
      data-testid={testId}
    >
      <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden />
      {label ? <span className="font-mono font-semibold">{label}</span> : null}
      <span className="min-w-0 break-words font-semibold" role="status">
        {t('delay.outOfPolicy.alert', {
          delay: formatDelayHours(report.delayMinutes),
          limit: formatDelayHours(report.limitMinutes),
        })}
      </span>
      <span className="text-muted-foreground" data-testid="out-of-policy-alert-source">
        {[
          t(`delay.timeline.source.${report.source}`, { defaultValue: report.source }),
          formatFlightDateTime(report.observedAt, delay.originIata),
        ].join(' · ')}
      </span>
      {canAct ? (
        <span className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={OUT_OF_POLICY_BUTTON}
            onClick={onCancel}
            disabled={dismiss.isPending}
            data-testid="out-of-policy-cancel"
          >
            {t('delay.toCancelled')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={OUT_OF_POLICY_BUTTON}
            onClick={() => dismiss.mutate({})}
            disabled={dismiss.isPending}
            data-testid="out-of-policy-dismiss"
          >
            {dismiss.isPending ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}
            {t('delay.outOfPolicy.dismiss')}
          </Button>
        </span>
      ) : null}
      {failure ? (
        <span className="basis-full break-words text-destructive" role="alert" data-testid="out-of-policy-dismiss-error">
          {t('delay.outOfPolicy.dismissFailed', {
            reason: t(failure.key, { defaultValue: t('delay.form.errors.server.generic', { code: failure.code }) }),
          })}
        </span>
      ) : null}
    </div>
  );
}
